import { create } from 'zustand'
import { useAccountMatterStore } from './use-account-matter-store';
import { sleep } from '../utils';
import { TAccountMatterStatus, TMatterStatus } from '../ingestion-tracking-api/types';
import { useMatterFilesStore } from './use-matter-files-store';

type IngestionTrackingStore = {
  matterIngestionStatus: TAccountMatterStatus | null
  matterIngestionDetails: TMatterStatus | null
  matterIngestionStatusLoading: boolean
  matterIngestionStatusErrorMessage: string
  percentIngested: number

  fetchMatterIngestionStatus: () => void
}

export const useIngestionTrackingStore = create<IngestionTrackingStore>(() => ({
  matterIngestionStatus: null,
  matterIngestionDetails: null,
  matterIngestionStatusLoading: false,
  matterIngestionStatusErrorMessage: '',
  percentIngested: 100,

  fetchMatterIngestionStatus: () => fetchData(),
}));

function calculatePercentIngested(matterIngestionStatus: TAccountMatterStatus | null, matterIngestionDetails: TMatterStatus | undefined) {
  let percentageIngested = 100

  if (matterIngestionDetails) {
    const { queued = 0, done = 0 } = matterIngestionDetails
    percentageIngested =
      queued === 0
        ? (matterIngestionStatus?.matterTracked === false ? 0 : 100)
        : Math.min(Math.round((done / queued) * 100), 100)
  } else if (
    matterIngestionStatus &&
    (!matterIngestionStatus.matterTracked ||
      matterIngestionStatus.fileIngested == false)
  ) {
    percentageIngested = 0
  }

  return percentageIngested
}

const fetchDataWithAbort = async (signal: AbortSignal) => {
  const { accountId, matterId } = useAccountMatterStore.getState();
  const { initialFileIds } = useMatterFilesStore.getState();

  if (!accountId || !matterId) {
    return
  }
  useIngestionTrackingStore.setState({ matterIngestionStatusLoading: true, matterIngestionStatusErrorMessage: '' })

  try {
    const maxAttempts = 10
    let attempt = 0
    while (true) {
      if (signal.aborted) {
        return;
      }
      const response = await fetch(`/api/matter/ingestion-tracking/matter`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          matterId,
          accountId,
          fileIds: initialFileIds,
          autoIngest: true
        })
      })
      // we don't pass signal to fetch as it creates too many errors in server logs, but exit after fetch if aborted
      if (signal.aborted) {
        return
      }
      if (response.status === 401) {
        if (attempt >= maxAttempts) {
          throw new Error('Unauthorized from ingestion-tracking route')
        }
        console.warn('Unauthorized from ingestion-tracking route, sleeping and retrying')
        attempt += 1
        await sleep(3000)
        continue
      }
      if (!response.ok) {
        throw new Error('failed to fetch matter ingestion status')
      }
      const data = (await response.json()) as TAccountMatterStatus
      const matterIngestionDetails = data?.statusList?.find(
        x => x.matterId == matterId
      )

      if (signal.aborted) {
        return;
      }

      useIngestionTrackingStore.setState({
        matterIngestionStatus: data,
        matterIngestionDetails: matterIngestionDetails ?? null,
        percentIngested: calculatePercentIngested(data, matterIngestionDetails),
        matterIngestionStatusLoading: false,
      });
      break
    }
  } catch (err) {
    if (err instanceof Error) {
      if (err.name !== 'AbortError') {
        useIngestionTrackingStore.setState({ matterIngestionStatusErrorMessage: 'Something went wrong while fetching matter ingestion status' })
      }
    }
  }
};

let currentController: AbortController | null = null;

const fetchData = () => {
  // need to keep this in closure so we can compare after finishing.
  const closureController = new AbortController();
  if (currentController) {
    currentController.abort();
  }
  currentController = closureController;

  fetchDataWithAbort(closureController.signal).catch((err) => {
    if (err?.name !== 'AbortError') {
      console.error('Error fetching ingestion tracking:', err);
    }
  }).finally(() => {
    if (!closureController.signal.aborted && closureController === currentController) {
      currentController = null;
    }
  })
}

// we need to subscribe to the useAccountMatterStore to see accountId/matterId changes
const subscribeToAccountId = () => {
  const unsubscribe = useAccountMatterStore.subscribe(
    (state, prevState) => {
      if (state.accountId !== prevState.accountId || state.matterId !== prevState.matterId) {
        fetchData();
      }
    }
  );

  // Return cleanup function
  return () => {
    // Abort any ongoing request
    if (currentController) {
      currentController.abort();
      currentController = null;
    }
    // Unsubscribe from the store
    unsubscribe();
  };
}

const subscribeToInitialFiles = () => {
  const unsubscribe = useMatterFilesStore.subscribe(
    (state, prevState) => {
      if (state.matterFiles !== prevState.matterFiles) {
        fetchData();
      }
    }
  );

  // Return cleanup function
  return () => {
    // Abort any ongoing request
    if (currentController) {
      currentController.abort();
      currentController = null;
    }
    // Unsubscribe from the store
    unsubscribe();
  };
}


export const initIngestionTrackingStore = () => {
  const disableAccountSub = subscribeToAccountId();
  const disableInitialFilesSub = subscribeToInitialFiles();
  fetchData();

  return [disableAccountSub, disableInitialFilesSub];
}
